import "./LabAdminDetailsAddComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import {CommonService} from "../../../../shared/services";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import Grid from "@mui/material/Grid";
import FormikAutoCompleteDropdown
    from "../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import _ from "lodash";
import commonService from "../../../../shared/services/common.service";
import {useSearchParams} from "react-router-dom";
import LinkComponent from "../../../../shared/components/link/LinkComponent";
import useRedirectUser from "../../../../shared/hooks/useRedirectUser";
import FeedbackComponent from "../../../../shared/components/feedback/feedbackComponent";

interface LabAdminDetailsAddComponentProps {
    next: Function;
    cancel: Function;
    labID: number | undefined;
    labDetails: any;
    prev: Function;
}

const validationSchema = Yup.object({
    adminId: Yup.mixed().nullable()
    // .required('Admin is Required'),
});

interface LabAdminDetailsFormSchema {
    adminId: string,
}

const formInitialValues: LabAdminDetailsFormSchema = {
    adminId: "",
}


const LabAdminDetailsAddComponent = (props: LabAdminDetailsAddComponentProps) => {
    const {next, cancel, prev, labDetails} = props;
    const {handleRedirectUserModule} = useRedirectUser();
    const [adminDetailsFormInitialValues, setAdminDetailsFormInitialValues] = useState<LabAdminDetailsFormSchema>(formInitialValues)
    const [searchParams] = useSearchParams();
    const lastCompletedStep = searchParams.get("lastCompletedStep");
    const [labAdminsList, setLabAdminsList] = useState<any>([]);
    const [labAdminsListLoading, setLabAdminsListLoading] = useState<boolean>(false);
    const [labAdminListLoadingFailed, setLabAdminListLoadingFailed] = useState<boolean>(false);
    const [labAdminListLoaded, setLabAdminListLoaded] = useState<boolean>(false);

    const onSubmit = useCallback((values: any, {
        setSubmitting,
    }: FormikHelpers<any>) => {
        setSubmitting(true);
        const payload = {adminId: values?.adminId?.id || null, lastCompletedStep: lastCompletedStep};
        commonService._lab.editLabDetails(labDetails?.id, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                // next(response?.data?.adminDetails);
                next({...labDetails, ...response.data})
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error.error, "error");
                setSubmitting(false);
            });
    }, [labDetails, next, lastCompletedStep]);

    const redirectUser = useCallback(() => {
        handleRedirectUserModule({type: 'labAdmin'})
    }, [handleRedirectUserModule])

    useEffect(() => {
        if (labDetails?.adminDetails && Object.keys(labDetails?.adminDetails).length) {
            const adminDetailsObj = _.cloneDeep(labDetails?.adminDetails);
            setAdminDetailsFormInitialValues({adminId: adminDetailsObj});
        }
    }, [labDetails]);

    const getLabAdminsList = useCallback(() => {
        setLabAdminsListLoading(true);
        setLabAdminListLoaded(false);
        setLabAdminListLoadingFailed(false)
        commonService._lab.getLabAdminsList({})
            .then((response: IAPIResponseType<any>) => {
                setLabAdminsList(response.data);
                setLabAdminListLoaded(true);
                setLabAdminsListLoading(false);
            })
            .catch((error: any) => {
                setLabAdminListLoadingFailed(true);
                setLabAdminsListLoading(false);
            });
    }, [])

    useEffect(() => {
        getLabAdminsList();
    }, [getLabAdminsList]);


    return (
        <div className={'admin-details-add-component screen t-form-screen'}>
            <FormTitleWithNavigationBack
                title={'Admin Details'}
                handleClick={prev}
                tooltipData={"These details relate to the person responsible for administering this lab."}
            />
            <div className="t-form t-form-back-navigation">
                <Formik
                    validationSchema={validationSchema}
                    initialValues={adminDetailsFormInitialValues}
                    validateOnChange={true}
                    validateOnBlur={true}
                    validateOnMount={true}
                    enableReinitialize={true}
                    onSubmit={onSubmit}
                >
                    {({isSubmitting, resetForm, values, isValid, errors, setFieldValue, validateForm}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            <Form noValidate={true} className={"lab-admin-details-add-holder"}>
                                <div className="t-form-controls-wrapper">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'adminId'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            label={'Lab Admin'}
                                                            placeholder={'Select Lab Admin'}
                                                            isDataLoaded={labAdminListLoaded}
                                                            isDataLoading={labAdminsListLoading}
                                                            isDataLoadingFailed={labAdminListLoadingFailed}
                                                            // required={true}
                                                            formikField={field}
                                                            data={labAdminsList}
                                                            valueKey={'id'}
                                                            getOptionLabel={(option => commonService.getFullName(option) || '')}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <div className="redirect-new-link-wrapper">
                                                <div>Didn't find admin?</div>
                                                <LinkComponent className={"redirect-link"}
                                                               onClick={redirectUser}>Create Admin</LinkComponent>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                            <FeedbackComponent className={"mrg-top-10 mrg-bottom-10"}>
                                                Adding an admin is recommended, but if unavailable, it can be skipped
                                                and added later.
                                            </FeedbackComponent>
                                        </Grid>
                                    </Grid>
                                </div>
                                <div className="form-action-section">
                                    <ButtonComponent
                                        type="button"
                                        color={"primary"}
                                        variant={"outlined"}
                                        fullWidth={true}
                                        disabled={isSubmitting}
                                        handleClick={cancel}
                                    >
                                        Close
                                    </ButtonComponent>

                                    <ButtonComponent
                                        type="submit"
                                        color={"primary"}
                                        variant={"contained"}
                                        fullWidth={true}
                                        disabled={!isValid}
                                        isLoading={isSubmitting}
                                    >
                                        Next
                                    </ButtonComponent>
                                </div>

                            </Form>
                        )
                    }
                    }
                </Formik>
            </div>
        </div>
    );

};

export default LabAdminDetailsAddComponent;
